import {IonCard, IonCardContent, IonCardHeader, IonList} from "@ionic/react";
import React, {useEffect, useRef, useState} from "react";
import createAxiosInstance from "../services/AxiosInstance";
import "./NewDashboard.css"
import LineChartComponent from "./LineChartComponent";
import LineChartTotals from "./LineChartTotals";
import QRCodeComponent from "./QRCodeComponent";
import {User} from "../model/User";
import {Move} from "../model/Move";
import {useStomp} from "../services/useStomp";
import {TotalsMove} from "../model/TotalsMove";
import {v4 as uuidv4} from 'uuid';

interface LobbyStats {
    lobbyMoves: Move[];
    amountOfDoublers: number;
    amountOfStreetThrows: number;
    slowestThrower: { username: string; averageThrowTime: number } | null;
    fastestThrower: { username: string; averageThrowTime: number } | null;
    avgAmountOfThrows: number;
    lobbyId: number;
}

const Dashboard: React.FC = () => {
    const [guests, setGuests] = useState<User[]>([]);
    const [eventMessages, setEventMessages] = useState<any[]>([]);
    const axios = createAxiosInstance();
    const [lobbyMoves, setLobbyMoves] = useState<Move[]>([]);
    const [totals, setTotals] = useState<TotalsMove[]>([]);
    const [amountOfDoublers, setAmountOfDoublers] = useState<number>(0);
    const [amountOfStreets, setAmountOfStreets] = useState<number>(0);
    const [slowestThrower, setSlowestThrower] = useState<any>(null)
    const [fastestThrower, setFastestThrower] = useState<any>(null)
    const [avgAmountOfTurns, setAvgAmountOfTurns] = useState<any>(null)
    const qrValueRef = useRef<string>('');
    const [lobbyId, setLobbyId] = useState<number>()
    const {subscribe, sendMessage} = useStomp();
    const [sessionId] = useState<string>(() => uuidv4());
    const currentLobbyIdRef = useRef<number | undefined>(undefined);

    useEffect(() => { currentLobbyIdRef.current = lobbyId; }, [lobbyId]);

    function fetchPresentGuests() {
        axios.get('/evening/present-guests')
            .then(res => {
                if (res.data[0] !== undefined) {
                    setGuests(res.data)
                }
            })
    }

    const fetchInitialStats = () => {
        axios.get<LobbyStats>("/lobbies/lobby-statistics")
            .then(res => {
                const data = res.data;
                setLobbyMoves(data.lobbyMoves);
                setAmountOfDoublers(data.amountOfDoublers);
                setAmountOfStreets(data.amountOfStreetThrows);
                setSlowestThrower(data.slowestThrower);
                setFastestThrower(data.fastestThrower);
                setAvgAmountOfTurns(data.avgAmountOfThrows);
                setLobbyId(data.lobbyId);
            })
            .catch(console.error);
    };

    const fetchTotalsData = async () => {
        try {
            const response = await axios.get('/lobbies/totals');
            setTotals(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchStatisticsByLobbyId = async (lobbyId: number | undefined) => {
        if (lobbyId === undefined || lobbyId === null) return;
        axios.get<LobbyStats>(`/lobbies/lobby-statistics?lobbyId=${lobbyId}`)
            .then(res => {
                const data = res.data;
                setLobbyMoves(data.lobbyMoves);
                setAmountOfDoublers(data.amountOfDoublers);
                setAmountOfStreets(data.amountOfStreetThrows);
                setSlowestThrower(data.slowestThrower);
                setFastestThrower(data.fastestThrower);
                setAvgAmountOfTurns(data.avgAmountOfThrows);
                setLobbyId(data.lobbyId);
            })
            .catch(console.error);
    }


    useEffect(() => {
        fetchPresentGuests()
        fetchInitialStats()
        fetchTotalsData()
    }, [])

    useEffect(() => {
        fetchStatisticsByLobbyId(lobbyId);
    }, [lobbyId]);

    useEffect(() => {
        if (!lobbyId) return;

        const unsubStats = subscribe(
            `/topic/dertigen/lobby/${lobbyId}/statistics`,
            (stats: LobbyStats) => {
                if (stats.lobbyId !== currentLobbyIdRef.current) return;

                setLobbyMoves(stats.lobbyMoves);
                setAmountOfDoublers(stats.amountOfDoublers);
                setAmountOfStreets(stats.amountOfStreetThrows);
                setSlowestThrower(stats.slowestThrower);
                setFastestThrower(stats.fastestThrower);
                setAvgAmountOfTurns(stats.avgAmountOfThrows);
            }
        );

        const unsubEvents = subscribe(
            `/topic/dashboard`,
            (raw: string) => {
                const now = new Date();
                const pad2 = (n: number) => n.toString().padStart(2, "0");
                const timestamp = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;

                let messageText: string;

                try {
                    const parsed = JSON.parse(raw) as { message: string };
                    messageText = parsed.message;
                } catch {
                    messageText = raw;
                }

                setEventMessages(prev => [
                    {message: messageText, time: timestamp},
                    ...prev
                ]);
            }
        );

        const unsubPresentUsers = subscribe(
            `/topic/dashboard/present-users`,
            (raw: User[]) => {
                try {
                    const parsed = raw;
                    console.log(parsed)
                    setGuests(parsed);
                } catch (err) {
                    console.error("Failed to parse present-users payload:", err);
                }
            }
        );

        return () => {
            unsubStats();
            unsubEvents();
            unsubPresentUsers();
        };
    }, [lobbyId]);

    useEffect(() => {
        return subscribe(
            `/topic/dashboard/${sessionId}`,
            (raw: string) => {
                const {lobbyId: scannedLobby} = JSON.parse(raw);
                setLobbyId(scannedLobby);
            }
        );
    }, [sessionId]);

    useEffect(() => {
        if (!sessionId) return;
        qrValueRef.current = sessionId;
    }, [sessionId]);

    return (
        <>
            <div className="stories-container">
                {guests && guests.length === 0 ? (
                    <p className="no-guests">Er is nog niemand ingecheckt.</p>
                ) : (guests &&
                    guests.map((user) => (
                        <div key={user.id} className="story">
                            <div
                                className={`profile-picture ${
                                    user?.balance !== undefined && user?.balance < 0 ? "negative" : "positive"
                                }`}
                            >
                                <img
                                    src={
                                        user.profilePictureUrl !== undefined
                                            ? `${process.env.REACT_APP_STORAGE}${user.profilePictureUrl}`
                                            : `${process.env.REACT_APP_STORAGE}default-profile.jpg`
                                    }
                                    alt={user.name}
                                />
                            </div>
                            <p className="username">{user.name}</p>
                        </div>
                    ))
                )}
            </div>
            <div className="dash-container">
                <div className="row-stats-feed">
                    <div className="stats-container visuals">
                        <IonCard className="visual-card qr-card">
                            <IonCardHeader>
                                <h3>Lobby koppelen dashboard</h3>
                            </IonCardHeader>
                            <IonCardContent>
                                {qrValueRef.current && <QRCodeComponent value={qrValueRef.current}/>}
                            </IonCardContent>
                        </IonCard>

                        <IonCard className="visual-card stat-card">
                            <IonCardHeader>
                                <div className="stat-header">
                                    <h3>Verdubbelaars</h3>
                                </div>
                            </IonCardHeader>
                            <IonCardContent>
                                <h2>{amountOfDoublers}</h2>
                                <p className="stat-subtext">Verdubbelaars in huidige lobby</p>
                            </IonCardContent>
                        </IonCard>

                        <IonCard className="visual-card stat-card">
                            <IonCardHeader>
                                <div className="stat-header">
                                    <h3>Straatjes</h3>
                                </div>
                            </IonCardHeader>
                            <IonCardContent>
                                <h2>{amountOfStreets}</h2>
                                <p className="stat-subtext">Straatjes in huidige lobby</p>
                            </IonCardContent>
                        </IonCard>

                        <IonCard className="visual-card stat-card">
                            <IonCardHeader>
                                <div className="stat-header">
                                    <h3>Langzaamste gooier</h3>
                                </div>
                            </IonCardHeader>
                            <IonCardContent>
                                <h2>{slowestThrower?.username}</h2>
                                <p className="stat-subtext">Gemiddeld {(slowestThrower?.averageThrowTime / 1000).toFixed(1)} seconden
                                    per worp</p>
                            </IonCardContent>
                        </IonCard>
                        <IonCard className="visual-card stat-card">
                            <IonCardHeader>
                                <div className="stat-header">
                                    <h3>Snelste gooier</h3>
                                </div>
                            </IonCardHeader>
                            <IonCardContent>
                                <h2>{fastestThrower?.username}</h2>
                                <p className="stat-subtext">Gemiddeld {(fastestThrower?.averageThrowTime / 1000).toFixed(1)} seconden
                                    per worp</p>
                            </IonCardContent>
                        </IonCard>
                        <IonCard className="visual-card stat-card">
                            <IonCardHeader>
                                <div className="stat-header">
                                    <h3>Gemiddelde beurten</h3>
                                </div>
                            </IonCardHeader>
                            <IonCardContent>
                                <h2>{avgAmountOfTurns}</h2>
                                <p className="stat-subtext">Gemiddeld aantal beurten per speler</p>
                            </IonCardContent>
                        </IonCard>
                    </div>
                    <div className="stats-container feed-container">
                        <IonList class="feed-column">
                            {eventMessages.length === 0 && <p className="no-events">(Nog) geen events gevonden</p>}
                            {eventMessages.map((chatMessage, index) => (
                                <IonCard className="event-card" key={index}>
                                    <h2>{chatMessage.message}</h2>
                                    <h2>{chatMessage.time}</h2>
                                </IonCard>
                            ))}
                        </IonList>
                    </div>
                </div>


                <div className={'row-chart'}>
                    {lobbyMoves && (
                        <div className='chart'>
                            <LineChartComponent lobbyMoves={lobbyMoves}/>
                        </div>
                    )}
                    {totals && (
                        <div className='chart'>
                            <LineChartTotals data={totals}/>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
export default Dashboard;
